package com.arenaproplay.admin.ui.theme

import androidx.compose.runtime.compositionLocalOf
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.silk.theme.colors.ColorMode

val LocalSportColors = compositionLocalOf { SportColors.Football }

fun Color.Companion.hex(hex: String): Color {
    val cleanHex = hex.removePrefix("#").uppercase()
    require(cleanHex.length == 6) { "Hex color must be 6 characters (e.g., '7A9DE4')" }
    return Color.rgb(
        cleanHex.substring(0, 2).toInt(16),
        cleanHex.substring(2, 4).toInt(16),
        cleanHex.substring(4, 6).toInt(16)
    )
}

fun Color.Companion.hexa(hex: String, alpha: Float): Color {
    val cleanHex = hex.removePrefix("#").uppercase()
    require(cleanHex.length == 6) { "Hex color must be 6 characters (e.g., '7A9DE4')" }
    return Color.rgba(
        cleanHex.substring(0, 2).toInt(16),
        cleanHex.substring(2, 4).toInt(16),
        cleanHex.substring(4, 6).toInt(16),
        alpha
    )
}

fun Color.toRgbString(): String {
    val rgb = this.toRgb()
    return "rgb(${rgb.red}, ${rgb.green}, ${rgb.blue})"
}

fun Color.toHexString(): String {
    val rgb = this.toRgb()
    fun toHex(n: Int): String {
        val hex = n.toString(16).uppercase()
        return if (hex.length == 1) "0$hex" else hex
    }
    return "${toHex(rgb.red)}${toHex(rgb.green)}${toHex(rgb.blue)}"
}

fun Color.toRgbaString(alpha: Float): String {
    val rgb = this.toRgb()
    return "rgba(${rgb.red}, ${rgb.green}, ${rgb.blue}, $alpha)"
}

fun Color.withAlpha(alpha: Float): Color {
    val rgb = this.toRgb()
    return Color.rgba(rgb.red, rgb.green, rgb.blue, alpha)
}

/**
 * Night Arena palette — dark command-center theme (#05080F scene, glass cards,
 * brand blue #0061FF, mint #00E5A4, yellow #E4CD40, coral #FF6B5B).
 *
 * NOTE on "tint" tokens: several call sites pipe tints through
 * [toRgbString]/[toHexString] which drop alpha, so all tints here are SOLID
 * colors — the alpha-on-dark accents pre-blended over #05080F.
 */
object AppColors {

    // ── Night Arena primary palette ────────────────────────────────────────
    // Accent / brand
    val Primary = Color.hex("0061FF")
    /** Blue accent tint bg — rgba(0,97,255,.20) pre-blended on #05080F. */
    val PrimaryTint = Color.hex("0A1E42")

    // Semantic aliases (kept from old API for compile compat)
    val PrimaryWhite = Color.hex("FFFFFF")
    val PrimaryBlack = Color.hex("05080F")
    // PrimaryYellow → PRO gold / warning yellow
    val PrimaryYellow = Color.hex("E4CD40")
    // PrimaryMint → success mint
    val PrimaryMint = Color.hex("00E5A4")
    val SecondaryMint = Color.hex("00E5A4")

    // Semantic status
    val Error = Color.hex("FF6B5B")
    val Success = Color.hex("00E5A4")
    val Warning = Color.hex("E4CD40")
    val Info = Color.hex("4D8CFF")

    // Top-level flat aliases from the brief
    /** Solid elevated dark surface (glass alpha is handled by GlassCard/CSS). */
    val Surface = Color.hex("0B1220")
    val Background = Color.hex("05080F")
    /** rgba(255,255,255,.08) pre-blended on #05080F. */
    val Border = Color.hex("1C2634")

    // Ink / text
    val Ink = Color.hex("F0F4FC")
    /** rgba(240,244,252,.55) pre-blended on #05080F. */
    val Muted = Color.hex("868A91")
    /** rgba(240,244,252,.45) pre-blended on #05080F. */
    val Faint = Color.hex("6F727A")

    // PRO gold
    val ProGold = Color.hex("E4CD40")

    // Shell-specific tokens
    /** Faint label / kbd hint text — e.g. group label in command palette. */
    val FaintKbd = Color.hex("5A6273")
    /** Breadcrumb separator colour. */
    val BreadcrumbSeparator = Color.hex("3A4356")
    /** Avatar gradient — start (top-left). */
    val AvatarGradientStart = Color.hex("0061FF")
    /** Avatar gradient — end (bottom-right). */
    val AvatarGradientEnd = Color.hex("4D8CFF")
    /** CSS gradient string for admin avatar circles. */
    val AvatarGradient = "linear-gradient(135deg,#0061FF,#4D8CFF)"

    /** Slightly elevated dark surface used for dividers and subtle containers. */
    val SurfaceMuted = Color.hex("0F1626")
    /** Error / danger tint background — rgba(255,107,91,.14) on #05080F. */
    val ErrorTint = Color.hex("2A1714")

    // ── Avatar gradient palette (5 entries; index derived from user id) ──────
    // Brand family on dark: blue / mint / yellow / coral / violet.
    /** Avatar gradient stop — blue (start). */
    val AvatarBlueStart = Color.hex("0061FF")
    /** Avatar gradient stop — blue (end). */
    val AvatarBlueEnd = Color.hex("4D8CFF")
    /** Avatar gradient stop — mint (start). */
    val AvatarSageStart = Color.hex("00C98F")
    /** Avatar gradient stop — mint (end). */
    val AvatarSageEnd = Color.hex("33EDB8")
    /** Avatar gradient stop — yellow (start). */
    val AvatarAmberStart = Color.hex("E4CD40")
    /** Avatar gradient stop — yellow (end). */
    val AvatarAmberEnd = Color.hex("F0DE6B")
    /** Avatar gradient stop — coral (start). */
    val AvatarCoralStart = Color.hex("FF6B5B")
    /** Avatar gradient stop — coral (end). */
    val AvatarCoralEnd = Color.hex("FF9A8D")
    /** Avatar gradient stop — violet (start). */
    val AvatarPeriwinkleStart = Color.hex("7C5CFF")
    /** Avatar gradient stop — violet (end). */
    val AvatarPeriwinkleEnd = Color.hex("A78BFA")

    /**
     * Named avatar gradient CSS strings built from token values above.
     * Index into this list using `abs(user.id.hashCode()) % AvatarPalette.size`.
     */
    val AvatarPalette: List<String> = listOf(
        "linear-gradient(135deg,#${AvatarBlueStart.toHexString()},#${AvatarBlueEnd.toHexString()})",
        "linear-gradient(135deg,#${AvatarSageStart.toHexString()},#${AvatarSageEnd.toHexString()})",
        "linear-gradient(135deg,#${AvatarAmberStart.toHexString()},#${AvatarAmberEnd.toHexString()})",
        "linear-gradient(135deg,#${AvatarCoralStart.toHexString()},#${AvatarCoralEnd.toHexString()})",
        "linear-gradient(135deg,#${AvatarPeriwinkleStart.toHexString()},#${AvatarPeriwinkleEnd.toHexString()})",
    )

    // ── Row hover bg (used in DataTable dense rows) ────────────────────────
    /** rgba(255,255,255,.04)-ish row hover pre-blended on Surface. */
    val SurfaceHover = Color.hex("101828")
    /** Slightly elevated rail background (right-rail cards). */
    val SurfaceRail = Color.hex("0C1322")
    /** Deep navy for dark gradient cards (e.g. PRO goal card). */
    val InkElevated = Color.hex("16233F")
    /** Blue hover state — light brand blue. */
    val PrimaryHover = Color.hex("4D8CFF")
    /** Lightest brand blue — active text/icons on blue tint backgrounds. */
    val PrimaryLight = Color.hex("8FB8FF")
    /** Soft blue — accent tint for gradients and sparklines. */
    val PrimarySoft = Color.hex("4D8CFF")
    /** Dimmed indigo used for chart secondary series on dark. */
    val ChartSoft = Color.hex("35538A")

    // Chat button (kept for compile compat — remapped to dark surfaces)
    val ChatButtonBackground = Color.hexa("F0F4FC", 0.05f)
    val ChatButtonGradientStart = Color.hex("0B1220")
    val ChatButtonGradientEnd = Color.hexa("0B1220", 0f)
    val ChatButtonShadowInner1 = Color.hexa("000000", 0.20f)
    val ChatButtonShadowInner2 = Color.hexa("000000", 0.28f)
    val ChatButtonShadowDrop = Color.hexa("000000", 0.35f)

    /** Renders black source icons as near-#F0F4FC (Night Arena ink). */
    const val IconDefaultFilter =
        "brightness(0) saturate(100%) invert(93%) sepia(5%) saturate(414%) hue-rotate(193deg) brightness(102%) contrast(97%)"
    /** Renders black source icons as coral #FF6B5B (danger). */
    const val IconDestructiveFilter =
        "brightness(0) saturate(100%) invert(58%) sepia(51%) saturate(1466%) hue-rotate(324deg) brightness(101%) contrast(101%)"

    object Text {
        val Primary = Color.hex("F0F4FC")
        val Secondary = Color.hex("868A91")
        val Muted = Color.hex("6F727A")
        val OnSurfaceMuted = Color.hex("868A91")
        val Subtle = Color.hex("6F727A")
        val NavLabelInactive = Color.hex("868A91")
    }

    /** Dark glass tooltip fill for charts. */
    val ChartTooltipFill = Color.hex("0B1322")

    object Light {
        // Kept for compile compat — remapped to the Night Arena dark values
        // (the dark theme is the only theme).
        val Background = Color.hex("05080F")
        val Surface = Color.hex("0B1220")
        val SurfaceVariant = Color.hex("0F1626")
        val OnBackground = Color.hex("F0F4FC")
        val OnSurface = Color.hex("F0F4FC")
        val OnPrimary = Color.hex("FFFFFF")

        val Border = Color.hex("1C2634")
        val Divider = Color.hex("141C29")

        val TextPrimary = Color.hex("F0F4FC")
        val TextSecondary = Color.hex("868A91")
        val TextTertiary = Color.hex("6F727A")

        val Shadow = Color.hexa("000000", 0.35f)
        val Overlay = Color.hexa("000000", 0.55f)
    }

    /**
     * "Dark" namespace is the active palette used throughout the app —
     * Night Arena: deep navy scene, elevated dark surfaces, light ink.
     */
    object Dark {
        val Background = Color.hex("05080F")
        val Surface = Color.hex("0B1220")
        val Surface2 = Color.hex("0B1220")
        val Surface3 = Color.hex("0F1626")
        val SurfaceVariant = Color.hex("0F1626")
        val Sidebar = Color.hex("070C16")
        val FeatureSurface = Color.hex("0E1A33")
        val OnBackground = Color.hex("F0F4FC")
        val OnSurface = Color.hex("F0F4FC")
        val OnPrimary = Color.hex("FFFFFF")

        val Border = Color.hex("1C2634")
        val BorderLight = Color.hex("141C29")
        val BorderAccent = Color.hexa("0061FF", 0.35f)
        val Divider = Color.hex("141C29")

        val TextPrimary = Color.hex("F0F4FC")
        val TextSecondary = Color.hex("868A91")
        val TextTertiary = Color.hex("6F727A")
        val TextMuted = Color.hex("6F727A")

        val Shadow = Color.hexa("000000", 0.35f)
        val Overlay = Color.hexa("000000", 0.55f)
    }

    object Accent {
        // Night Arena semantic accents on dark
        val Mint = Color.hex("00E5A4")
        val MintDeep = Color.hex("00C98F")
        val Amber = Color.hex("E4CD40")
        val Coral = Color.hex("FF6B5B")
        val Violet = Color.hex("7C5CFF")
        val Cyan = Color.hex("4D8CFF")

        // Semantic tints — alpha-on-dark accents pre-blended over #05080F
        // (solid so toRgbString/toHexString call sites keep working).
        /** rgba(0,229,164,.14) on #05080F. */
        val SuccessTint = Color.hex("072A21")
        /** rgba(228,205,64,.12) on #05080F. */
        val WarningTint = Color.hex("262311")
        /** rgba(255,107,91,.14) on #05080F. */
        val DangerTint = Color.hex("2A1714")
        /** rgba(0,97,255,.20) on #05080F. */
        val AccentTint = Color.hex("0A1E42")
    }

    val DarkTheme = ThemeColors(
        background = Dark.Background,
        surface = Dark.Surface2,
        surfaceVariant = Dark.SurfaceVariant,
        onBackground = Dark.OnBackground,
        onSurface = Dark.OnSurface,
        onPrimary = Dark.OnPrimary,
        border = Dark.Border,
        divider = Dark.Divider,
        textPrimary = Dark.TextPrimary,
        textSecondary = Dark.TextSecondary,
        textTertiary = Dark.TextTertiary,
        shadow = Dark.Shadow,
        overlay = Dark.Overlay
    )

    fun getColors(colorMode: ColorMode): ThemeColors {
        return when (colorMode) {
            ColorMode.LIGHT -> ThemeColors(
                background = Light.Background,
                surface = Light.Surface,
                surfaceVariant = Light.SurfaceVariant,
                onBackground = Light.OnBackground,
                onSurface = Light.OnSurface,
                onPrimary = Light.OnPrimary,
                border = Light.Border,
                divider = Light.Divider,
                textPrimary = Light.TextPrimary,
                textSecondary = Light.TextSecondary,
                textTertiary = Light.TextTertiary,
                shadow = Light.Shadow,
                overlay = Light.Overlay
            )
            ColorMode.DARK -> DarkTheme
        }
    }
}

data class ThemeColors(
    val background: Color,
    val surface: Color,
    val surfaceVariant: Color,
    val onBackground: Color,
    val onSurface: Color,
    val onPrimary: Color,
    val border: Color,
    val divider: Color,
    val textPrimary: Color,
    val textSecondary: Color,
    val textTertiary: Color,
    val shadow: Color,
    val overlay: Color
)

data class SportColorScheme(
    val primary: Color,
    val onPrimary: Color,
    val secondary: Color,
    val onSecondary: Color,
)

object SportColors {
    val Football = SportColorScheme(
        primary = Color.hex("0061FF"),
        onPrimary = Color.hex("FFFFFF"),
        secondary = Color.hex("0052D9"),
        onSecondary = Color.hex("FFFFFF"),
    )
    val Tennis = SportColorScheme(
        primary = Color.hex("00E5A4"),
        onPrimary = Color.hex("03140B"),
        secondary = Color.hex("00C98F"),
        onSecondary = Color.hex("03140B"),
    )
    val Volleyball = SportColorScheme(
        primary = Color.hex("E4CD40"),
        onPrimary = Color.hex("1A1608"),
        secondary = Color.hex("D4BC2E"),
        onSecondary = Color.hex("1A1608"),
    )
    val Basketball = SportColorScheme(
        primary = Color.hex("FF6B5B"),
        onPrimary = Color.hex("230B07"),
        secondary = Color.hex("E85847"),
        onSecondary = Color.hex("230B07"),
    )
    val Padel = Football

    fun forSport(sport: String): SportColorScheme = when (sport.lowercase()) {
        "football", "soccer" -> Football
        "tennis" -> Tennis
        "volleyball" -> Volleyball
        "basketball" -> Basketball
        "padel" -> Padel
        else -> Football
    }
}

object BusinessColors {
    object Blue {
        val B100 = Color.hex("0061FF")
        val B200 = Color.hex("0052D9")
        val B300 = Color.hex("0043B3")
        val B400 = Color.hex("00368C")
        val B500 = Color.hex("002966")
    }

    object Status {
        val Pending = Color.hex("E4CD40")
        val Approved = Color.hex("00E5A4")
        val Declined = Color.hex("FF6B5B")
        val Completed = Color.hex("4D8CFF")
        val Cancelled = Color.hex("6F727A")
    }

    object Surface {
        val ClientRowHover = Color.hex("101828")
        val DayContainerHover = Color.hex("131B2E")
        val EmptyImagePrimary = Color.hex("1C2634")
        val EmptyImageSecondary = Color.hex("141C29")
        val ModalBackground = Color.hex("0B1220")
    }
}

object AppGradients {
    val SurfaceGradient01 =
        "linear-gradient(55deg, rgba(0, 97, 255, 0.14), rgba(255, 255, 255, 0.02))"
    val SurfaceGradient02 =
        "linear-gradient(0deg, rgba(0, 97, 255, 0.10), rgba(255, 255, 255, 0.02))"
    val SurfaceInnerGradientL =
        "linear-gradient(-115deg, rgba(0, 97, 255, 0.08), rgba(0, 229, 164, 0.08))"
    val InteractiveSecondary =
        "linear-gradient(-115deg, rgba(0, 97, 255, 0.06), rgba(0, 229, 164, 0.06))"
    val BorderGradient01 =
        "linear-gradient(-88deg, rgba(77, 140, 255, 0.60) 59%, rgba(0, 97, 255, 0.10) 95%)"
    val ButtonBorderGradientPrimary =
        "linear-gradient(180deg, rgba(255, 255, 255, 0.10), rgba(255, 255, 255, 0.02))"
}
